import { transformAPIKeyToCamel } from "@api/utils/transformAPIKeyToCamel";
import { transformCamelToSnake } from "@api/utils/transformCamelToSnake";
import { ListResponseType } from "src/types";

import CoreAPI from "./CoreAPI";

const apiClient = new CoreAPI();

export interface VirtualWarehouseItem {
  alloId?: number;
  stockId?: number | null;
  sku: string;
  retailerName: string | null;
  productName: string;
  effectiveDate: string | null;
  batch: string | null;
  totalStock: number;
  allocatedQty: number;
  canUseQty: number;
}

export const getItemKey = (item: VirtualWarehouseItem): string =>
  item.alloId !== undefined ? `allo-${item.alloId}` : `stock-${item.stockId}`;

export type VirtualWarehouseListResult = ListResponseType<VirtualWarehouseItem>;

export interface VirtualWarehouseFilter {
  sku?: string[];
  regionType?: number;
  retailerId?: number;
  productName?: string;
  effectiveDateAfter?: string;
  effectiveDateBefore?: string;
  batch?: string;
  limit: number;
  offset: number;
}

export interface VirtualWarehouseListParams {
  sku?: string;
  regionType?: number | string;
  retailerId?: number | string;
  productName?: string;
  effectiveDateAfter?: string;
  effectiveDateBefore?: string;
  batch?: string;
  limit: number;
  offset: number;
}

export interface AllocationPayload {
  alloId?: number;
  stockId?: number;
  destRetailerId: number;
  qty: number;
}

export interface AllocateLogItem {
  id: number;
  sku: string;
  productName: string;
  sourceId: number;
  sourceName: string;
  destId: number;
  destName: string;
  staff: string;
  qty: number;
  createdAt: string;
}

export type AllocateLogListResult = ListResponseType<AllocateLogItem>;

export interface AllocateLogFilter {
  sku?: string[];
  productName?: string;
  sourceId?: number;
  destId?: number;
  createdAtAfter?: string;
  createdAtBefore?: string;
  staffId?: number;
  limit: number;
  offset: number;
}

export interface AllocateLogRequestParams {
  sku?: string;
  productName?: string;
  sourceId?: number | string;
  destId?: number | string;
  createdAtAfter?: string;
  createdAtBefore?: string;
  staffId?: number;
  limit: number;
  offset: number;
}

interface AllocateManagementAPI {
  fetchVirtualWarehouseList: (params: VirtualWarehouseListParams) => Promise<VirtualWarehouseListResult>;
  submitAllocation: (payloads: AllocationPayload[]) => Promise<void>;
  fetchAllocateLogList: (params: AllocateLogRequestParams) => Promise<AllocateLogListResult>;
  importAllocateOrder: (file: File) => Promise<void>;
}

const allocateManagementApi: AllocateManagementAPI = {
  fetchVirtualWarehouseList: async (params) => {
    const response = await apiClient.getData("/manage/vs/", transformCamelToSnake(params));
    return transformAPIKeyToCamel(response.data.result);
  },
  submitAllocation: async (payloads) => {
    await apiClient.postData("/manage/vs/allocate/", transformCamelToSnake(payloads));
  },
  fetchAllocateLogList: async (params) => {
    const response = await apiClient.getData("/manage/vs/log/", transformCamelToSnake(params));
    const result = transformAPIKeyToCamel(response.data.result);
    return {
      count: result.count ?? 0,
      next: result.next ?? null,
      previous: result.previous ?? null,
      results: result.results ?? [],
    };
  },
  importAllocateOrder: async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    await apiClient.postData("/manage/vs/upload/", formData);
  },
};

export default allocateManagementApi;
